import React, { useRef, useEffect, useState } from 'react';
import { Eraser } from 'lucide-react';

/**
 * SignaturePad — minimal HTML5-canvas signature capture.
 *
 * Props:
 *  - value:  data:image/png;base64,... (initial signature; if set, the pad
 *            renders with that image as the starting state)
 *  - onChange(dataUrl): called whenever the user finishes a stroke or clears.
 *  - height: pixel height of the pad (default 160)
 *  - testid: data-testid prefix for the canvas + buttons
 */
const SignaturePad = ({ value, onChange, height = 160, testid = 'signature' }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const [hasStrokes, setHasStrokes] = useState(!!value);

  // Initialise canvas from `value` when component mounts or value changes externally
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Hi-DPI awareness
    const ratio = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    if (canvas.width !== cssWidth * ratio || canvas.height !== height * ratio) {
      canvas.width = cssWidth * ratio;
      canvas.height = height * ratio;
      ctx.scale(ratio, ratio);
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#0f172a';

    // Clear and optionally draw existing value
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (value && typeof value === 'string' && value.startsWith('data:image/')) {
      const img = new window.Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.clientWidth, height);
        setHasStrokes(true);
      };
      img.src = value;
    }
  }, [height]);

  const point = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches && e.touches[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = point(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPtRef.current = p;
    setHasStrokes(true);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPtRef.current = null;
    if (onChange) {
      try {
        onChange(canvasRef.current.toDataURL('image/png'));
      } catch (err) {
        // Tainted-canvas CORS errors are expected when an external signature
        // image is drawn back in; log at debug so anyone with DevTools open
        // can still see what happened without breaking the user flow.
        if (typeof console !== 'undefined' && console.debug) {
          console.debug('SignaturePad toDataURL failed (likely tainted canvas):', err);
        }
      }
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
    if (onChange) onChange('');
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <canvas
          ref={canvasRef}
          style={{ height, width: '100%', touchAction: 'none' }}
          className="bg-white/95 border border-white/20 rounded-lg cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={`${testid}-canvas`}
        />
        {!hasStrokes && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
            Draw your signature here
          </div>
        )}
      </div>
      <div className="flex justify-between items-center">
        <span className="text-xs text-slate-400">
          {hasStrokes ? 'Signature captured' : 'Awaiting signature'}
        </span>
        <button
          type="button"
          onClick={clear}
          disabled={!hasStrokes}
          className="inline-flex items-center gap-1 text-xs bg-white/5 hover:bg-white/10 disabled:opacity-40 border border-white/10 text-slate-300 px-2.5 py-1 rounded transition"
          data-testid={`${testid}-clear`}
        >
          <Eraser size={12} /> Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
